import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box,
  useTheme,
  Divider,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type StatusType = 'success' | 'warning' | 'error' | 'info';
type IconType = 'cpu' | 'memory' | 'disk' | 'network' | 'users' | 'accuracy' | 'api' | 'custom';

interface MetricCardProps {
  title: string;
  value: number;
  unit: string;
  status: StatusType;
  icon?: IconType;
  change?: number;
  customIcon?: React.ReactNode;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  unit,
  status,
  icon,
  change,
  customIcon
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      case 'info':
        return theme.palette.info.main;
      default:
        return theme.palette.primary.main;
    }
  };
  
  const getTrendIcon = () => {
    if (change && change > 0) {
      return <TrendingUpIcon fontSize={isMobile ? "small" : "medium"} />;
    } else if (change && change < 0) {
      return <TrendingDownIcon fontSize={isMobile ? "small" : "medium"} />;
    } else {
      return <TrendingFlatIcon fontSize={isMobile ? "small" : "medium"} />;
    }
  };
  
  const getMetricIcon = () => {
    const iconSize = isMobile ? "medium" : "large";
    
    switch (icon) {
      case 'cpu':
        return <MemoryIcon fontSize={iconSize} />;
      case 'memory':
        return <StorageIcon fontSize={iconSize} />;
      case 'disk':
        return <StorageIcon fontSize={iconSize} />;
      case 'network':
        return <NetworkCheckIcon fontSize={iconSize} />;
      case 'users':
        return <PeopleIcon fontSize={iconSize} />;
      case 'accuracy':
        return <CheckCircleIcon fontSize={iconSize} />;
      case 'api':
        return <SpeedIcon fontSize={iconSize} />;
      case 'custom':
        return customIcon;
      default:
        return null;
    }
  };
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: 2,
        boxShadow: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMobile ? 1 : 2 }}>
          <Typography 
            color="text.secondary" 
            variant={isMobile ? "body2" : "subtitle1"}
            fontWeight="medium"
          >
            {title}
          </Typography>
          
          {getMetricIcon() && (
            <Box 
              sx={{ 
                color: getStatusColor(),
                bgcolor: `${getStatusColor()}15`,
                display: 'flex',
                p: isMobile ? 0.5 : 1,
                borderRadius: 1
              }}
            >
              {getMetricIcon()}
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="div" 
            sx={{ fontWeight: 'medium' }}
          >
            {typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : value}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ ml: 0.5 }}
          >
            {unit}
          </Typography>
        </Box>
        
        {change !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Tooltip title={`${change > 0 ? 'Increased' : change < 0 ? 'Decreased' : 'No change'} since last period`}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: change > 0 
                    ? theme.palette.success.main 
                    : change < 0 
                      ? theme.palette.error.main 
                      : theme.palette.info.main,
                  mr: 1
                }}
              >
                {getTrendIcon()}
                <Typography 
                  variant={isMobile ? "caption" : "body2"} 
                  sx={{ ml: 0.5 }}
                >
                  {Math.abs(change)}%
                </Typography>
              </Box>
            </Tooltip>
            
            <Typography variant="caption" color="text.secondary">
              vs. last period
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 1 }} />
        
        <Box 
          sx={{ 
            height: 4, 
            width: '100%', 
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            overflow: 'hidden',
            mt: 1
          }}
        >
          <Box 
            sx={{ 
              height: '100%', 
              width: `${Math.min(100, value)}%`, 
              bgcolor: getStatusColor(),
              borderRadius: 2
            }} 
          />
        </Box>
      </CardContent>
    </Card>
  );
};
